// 侧栏收起后那条窄栏（`SidebarRail`）的**纯逻辑**：把 `volumes / chaptersMap /
// selectedChapter` 折算成"当前第几卷第几章 + 该卷章节号列表"。
//
// 为什么单独一个 `.ts`：仓库的 vitest 只收 `src/**/*.test.ts`（见
// `apps/web/vitest.config.ts` 的 include），纯逻辑留在 `.tsx` 里就没法单测。
// 这些函数又恰好是"脏数据边界"的集中地 —— 一卷都没有、选中的章节属于一个已被
// 删掉的卷、`chapter_number` 是 0 或 null —— 在窄栏里任何一处崩掉都会让**整个
// 创作页**白屏（窄栏是常驻结构），所以必须能锁住。
import type { Chapter, Volume } from '../../types';

/** 窄栏列表里的一个卷分组 */
export interface SidebarRailVolumeGroup {
  volumeId: string;
  /** 卷序号（1 起，按 order_index） */
  volumeIndex: number;
  volumeTitle: string;
  /** 该卷章节，按章号升序 */
  chapters: Chapter[];
}

/** 窄栏用的派生数据 */
export interface SidebarRailNav {
  /** 当前卷序号（1 起）；0 = 未知（没选中章节，或该卷已不在卷列表里） */
  volumeIndex: number;
  /** 当前卷 id；未知为 null（此时分卷按钮不可展开） */
  volumeId: string | null;
  /** 当前卷标题；未知为空串 */
  volumeTitle: string;
  /** 当前章号；0 = 未知 */
  chapterNumber: number;
  /** 当前卷的章节，按章号升序 */
  chapters: Chapter[];
  /**
   * **全部卷**及其章节（按 order_index）。
   *
   * 这是修掉"新增分卷后收起侧栏就只能看到最新那一卷"的关键：早先只给**当前卷**的章节，
   * 于是切到新卷以后就再也回不到别的卷 —— 窄栏本该是"收起后仍可导航"，
   * 却成了单卷的死胡同。现在展开时把每一卷都列出来（当前卷高亮）。
   */
  groups: SidebarRailVolumeGroup[];
}

/** 数字未知时窄栏里显示的占位符（比 `0` 更不容易被误读成"第 0 章"） */
export const RAIL_UNKNOWN = '–';

/**
 * 卷排序：`order_index` 升序 —— 与章节树 / 导出用的规则同一套（`order_index`
 * 是用户拖出来的顺序，不保证等于数组下标）。
 */
export function sortVolumes(volumes: readonly Volume[]): Volume[] {
  return [...volumes].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
}

/**
 * 章号：非法值一律折算成 0（未知）。
 * 0 / 负数 / NaN / null 都当未知处理 —— 章号从 1 开始，`0` 在界面上没有意义。
 */
export function chapterNumberOf(chapter: Chapter | null | undefined): number {
  const n = chapter?.chapter_number;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * 章节排序：章号升序，未知章号（0）排在最前。
 * 入参允许 undefined —— `chaptersMap[volumeId]` 在数据还没加载完时就是 undefined。
 */
export function sortChapters(chapters: readonly Chapter[] | null | undefined): Chapter[] {
  if (!chapters || chapters.length === 0) return [];
  return [...chapters].sort((a, b) => chapterNumberOf(a) - chapterNumberOf(b));
}

/**
 * 按 `order_index` 升序后，某卷的序号（1 起）。
 * 卷不存在（含 `volumeId` 为 null / 空串）时返回 0 —— 调用方用 0 表示"未知"。
 */
export function findVolumeIndex(
  volumes: readonly Volume[],
  volumeId: string | null | undefined,
): number {
  if (!volumeId) return 0;
  const i = sortVolumes(volumes).findIndex(v => v.id === volumeId);
  return i < 0 ? 0 : i + 1;
}

/**
 * 一次算出窄栏要用的全部派生数据。
 *
 * 注意「找不到卷」的分支：选中的章节指向一个不在 `volumes` 里的 `volume_id`
 * （卷被删掉、或作品切换的一瞬间），此时**不**回落到第一卷 —— 否则窄栏会显示
 * "第 1 卷"、"第 0 章"这种假信息；一律按未知（0 / null / 空列表）处理。
 */
export function railNav(
  volumes: readonly Volume[],
  chaptersMap: Readonly<Record<string, Chapter[]>> | null | undefined,
  selectedChapter: Chapter | null | undefined,
): SidebarRailNav {
  const sorted = sortVolumes(volumes);
  const volumeId = selectedChapter?.volume_id ?? null;
  const volumeIndex = findVolumeIndex(volumes, volumeId);
  const volume = volumeIndex > 0 ? sorted[volumeIndex - 1] : null;
  const groups: SidebarRailVolumeGroup[] = sorted.map((v, i) => ({
    volumeId: v.id,
    volumeIndex: i + 1,
    volumeTitle: v.title ?? '',
    chapters: sortChapters(chaptersMap?.[v.id]),
  }));
  return {
    volumeIndex,
    volumeId: volume?.id ?? null,
    volumeTitle: volume?.title ?? '',
    chapterNumber: chapterNumberOf(selectedChapter),
    chapters: volume ? sortChapters(chaptersMap?.[volume.id]) : [],
    groups,
  };
}

/** 窄栏里的数字标签：> 0 显示数字，否则显示占位符 */
export function railNumberLabel(n: number): string {
  return Number.isFinite(n) && n > 0 ? String(n) : RAIL_UNKNOWN;
}
